import { boolean, InferType, mixed, object, ObjectSchema, string } from "yup";
import { AddressRequiredText, LowesFileUploadRequiredError, PONumberRequiredText } from "constants/text";
import { type FormSchema as ShipToSchema, shipToFormSchemaObject } from "components/Replacements/ShipToForm/schema";
import {
	CustomDesignerLengthText,
	DesignerIsNotHereText,
	DesignerNumberMatchText,
	DesignerNumberRequiredText
} from "../constants";
import { models } from "types/api/viewModels.ts";
import { components } from "types/declarations/api";

export const PO_NUMBER_MAX_LENGTH = 25;
export const JOB_NAME_MAX_LENGTH = 30;
export const CUSTOM_DESIGNER_LENGTH = 6;

export interface FormSchema extends ShipToSchema {
	designer?: models["DesignerViewModel"] | string | null;
	jobName?: string | null;
	poNumber?: string | null;
	address?: models["ShipToViewModel"];
	customDesigner?: string | null;
	billTo?: models["BillToViewModel"] | null;
	businessPurpose?: components["schemas"]["OrderBusinessPurpose"] | null;
	lowesFileUploads?: boolean | null;
}

export const schema: ObjectSchema<FormSchema> = object({
	...shipToFormSchemaObject,
	designer: mixed<models["DesignerViewModel"] | string>().nullable(),
	jobName: string().max(JOB_NAME_MAX_LENGTH),
	poNumber: string().max(PO_NUMBER_MAX_LENGTH).required(PONumberRequiredText),
	address: mixed<models["ShipToViewModel"]>().when("isCustomerPickup", {
		is: true,
		then: (schema) => schema,
		otherwise: (schema) => schema.required(AddressRequiredText)
	}),
	customDesigner: string().when("designer", {
		is: DesignerIsNotHereText,
		then: (schema) =>
			schema
				.required(DesignerNumberRequiredText)
				.matches(/^1\d{0,5}$/, DesignerNumberMatchText)
				.length(CUSTOM_DESIGNER_LENGTH, CustomDesignerLengthText(CUSTOM_DESIGNER_LENGTH)),
		otherwise: (schema) => schema.notRequired().nullable()
	}),
	billTo: mixed<models["BillToViewModel"]>().required().nullable(),
	businessPurpose: mixed<components["schemas"]["OrderBusinessPurpose"]>()
		.nullable()
		.when("$isBusinessPurposeRequired", {
			is: true,
			then: (schema) => schema.required(),
			otherwise: (schema) => schema.notRequired()
		}),
	lowesFileUploads: boolean()
		.nullable()
		.when(["$isLowesUser", "$isReplacement"], {
			is: (isLowesUser: boolean, isReplacement: boolean) => isLowesUser && !isReplacement,
			then: (schema) => schema.required(LowesFileUploadRequiredError),
			otherwise: (schema) => schema.notRequired()
		})
});

export type ShippingDetailsSchema = InferType<typeof schema>;
